import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Cube, Buildings, UploadSimple, Sparkle, SquaresFour, ArrowRight, ArrowLeft, CheckCircle,
} from "@phosphor-icons/react";

const STEPS = [
  {
    icon: Cube,
    title: "Welcome to LispForge",
    body: "Generate production-ready AutoLISP for AutoCAD, Civil 3D and progeCAD — grounded in each client's own layers, styles and existing commands. Here's the 30-second tour.",
  },
  {
    icon: Buildings,
    title: "1 · Pick or create a client",
    body: "Everything is organised per client. In the Start-here hub choose an existing client chip or type a new name and Create. The AI grounds every routine in that client's standards.",
  },
  {
    icon: UploadSimple,
    title: "2 · Add their docs & existing LISP",
    body: "Upload the client's reference files, finished/output CAD (to match layers), and any existing .lsp — even progeCAD ones. Uploaded LISP commands are reused on every generation, and you can Batch-port them all to your target platform in one click.",
  },
  {
    icon: Sparkle,
    title: "3 · Describe, set options & Generate",
    body: "Tap starter cards and Tool presets to stack them into the prompt box (they add, not replace), edit freely, choose your target platforms / scale / refine options — all right there — then Generate. Results auto-save to the client's history.",
  },
  {
    icon: SquaresFour,
    title: "Your tools live in the top menus",
    body: "The toolbar is grouped into Generate, Survey Tools, Client and Help. Open Help → \"Take the tour\" (or the ? button) to see this again anytime.",
  },
];

export const FirstRunTour = ({ open, onClose }) => {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  const Icon = step.icon;
  const finish = () => { setI(0); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-md rounded-[6px]" data-testid="first-run-tour">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2">
            <span className="h-8 w-8 flex items-center justify-center rounded-[4px] bg-[var(--primary)]/15">
              <Icon size={18} weight="bold" className="text-[var(--primary)]" />
            </span>
            {step.title}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-[13px] leading-relaxed pt-2" data-testid="tour-body">
            {step.body}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-1.5 py-1" data-testid="tour-dots">
          {STEPS.map((_, n) => (
            <span key={n} className={`h-1.5 rounded-full transition-all ${n === i ? "w-5 bg-[var(--primary)]" : "w-1.5 bg-[var(--border)]"}`} />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <button onClick={finish} data-testid="tour-skip"
            className="text-[12px] font-mono text-[var(--text-dim)] hover:text-[var(--text)]">Skip</button>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setI((n) => n - 1)} data-testid="tour-back"
                className="h-9 rounded-[3px] gap-1.5 text-[13px] font-mono">
                <ArrowLeft size={14} /> Back
              </Button>
            )}
            {last ? (
              <Button size="sm" onClick={finish} data-testid="tour-done"
                className="h-9 rounded-[3px] gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-[13px] font-mono">
                <CheckCircle size={15} weight="bold" /> Get started
              </Button>
            ) : (
              <Button size="sm" onClick={() => setI((n) => n + 1)} data-testid="tour-next"
                className="h-9 rounded-[3px] gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-[13px] font-mono">
                Next <ArrowRight size={14} weight="bold" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
